import { ensureError } from '@hover/utilities';
import invariant from 'tiny-invariant';

import { logError } from 'src/shared/telemetry/logger';

import type { SegmentationSurface } from '../schemas/segmentation';
import { segmentationSurfaceSchema } from '../schemas/segmentation';

import { warn } from './logging';

/**
 * Parse the SVG text to an in-memory SVG document. Note that this does not
 * execute any JavaScript and therefore does not open us up to any cross-site
 * scripting attacks from the SVG contents.
 *
 * @param svg SVG as a string
 *
 * @returns SVG element
 */
const parseSvg = (svg: string): SVGElement => {
  const parser = new DOMParser();
  const element = parser.parseFromString(svg, 'image/svg+xml').documentElement;

  invariant(
    element instanceof SVGElement,
    'processSvgMask: unable to parse provided SVG string into an SVG element',
  );

  return element;
};

/**
 * Extracts the width and height from an SVG element. It uses the `viewBox`
 * attribute if available, otherwise falls back to the `width` and `height`
 * attributes.
 *
 * @param element The SVG element to extract dimensions from.
 * @returns An object containing the width and height.
 */
export const extractSvgDimensions = (
  element: SVGElement,
): { width: number; height: number } => {
  if (element.hasAttribute('viewBox')) {
    const coordinates = element.getAttribute('viewBox')?.split(' ') ?? [];

    invariant(
      coordinates.length === 4,
      'extractSvgDimensions: invalid viewBox attribute format',
    );

    const width = parseFloat(coordinates[2]);
    const height = parseFloat(coordinates[3]);

    invariant(
      !isNaN(width) && !isNaN(height),
      'extractSvgDimensions: invalid width or height in viewBox attribute',
    );

    return { width, height };
  }

  const widthAttribute = element.getAttribute('width');
  const heightAttribute = element.getAttribute('height');

  invariant(
    widthAttribute && heightAttribute,
    'extractSvgDimensions: both width and height attributes are required',
  );

  const width = parseFloat(widthAttribute);
  const height = parseFloat(heightAttribute);

  invariant(
    !isNaN(width) && !isNaN(height),
    'extractSvgDimensions: invalid width or height attributes',
  );

  return { width, height };
};

const parsePointsFromPath = (path: string) => {
  const components = path.replace(/\n/g, ' ').trim().split(/\s+/);
  const coordinates = new Set<string>();

  // Gather unique coordinates from `M` and `L` commands which represent "move"
  // and "line" commands with absolute coordinates
  for (let i = 0; i < components.length; i += 1) {
    if (components[i] === 'M' || components[i] === 'L') {
      const x = parseFloat(components[i + 1]);
      const y = parseFloat(components[i + 2]);

      coordinates.add(`${x},${y}`);
      i += 2;
    }
  }

  return Array.from(coordinates).map(coordinate => {
    const [x, y] = coordinate.split(',').map(Number);
    return { x, y };
  });
};

interface Path {
  d: string;
  path: SVGPathElement;
}

interface PathGroup {
  material: string;
  surface: SegmentationSurface;
  paths: Path[];
  masks?: Path[];
}

export interface Segment {
  id: string;
  material: string;
  surface: SegmentationSurface;
  paths: string[];
  masks?: string[];
}

export interface SegmentationData {
  dimensions: { width: number; height: number };
  segments: Segment[];
}

/**
 * Process a segmentation mask SVG from a string and extract the information we
 * need to display the interface elements for highlighting and interacting with
 * the segmented regions
 *
 * @note This function can only run in the browser as it relies on DOM APIs
 *
 * @param svg SVG string to process
 * @param attribute path attribute to use to identify unique segmentation classes
 *
 * @returns an object containing the dimensions of the SVG and the path data for
 * each segment grouped by segmentation class
 */
export const processMaskSvg = (
  svg: string,
  attribute: string = 'fill',
): SegmentationData => {
  try {
    const element = parseSvg(svg);

    const segments = Array.from(element.querySelectorAll('path')).reduce(
      (acc, path) => {
        const attributes = {
          d: path.getAttribute('d'),
          key: path.getAttribute(attribute),
          material: path.getAttribute('data-material'),
          surface: path.getAttribute('data-surface'),
        };

        Object.entries(attributes).forEach(([key, value]) => {
          if (!value) {
            warn(
              `processSvgMask: encountered a path without a "${key}" attribute, it will be excluded`,
            );
          }
        });

        const { d, key, material, surface } = attributes;

        if (!d || !key || !material || !surface) return acc;

        if (!segmentationSurfaceSchema.isValidSync(surface)) {
          warn(
            `processSvgMask: encountered an unsupported surface attribute ("${surface}"), you may need to update the Yup schema`,
          );
          return acc;
        }

        const group = acc[key];

        if (
          group &&
          (group.material !== material || group.surface !== surface)
        ) {
          warn(
            `processSvgMask: encountered a path ("${key}") with mismatched surface or material, it will be excluded`,
          );
          return acc;
        }

        if (!group) {
          return {
            ...acc,
            [key]: { material, surface, paths: [{ d, path }] },
          };
        }

        const points = parsePointsFromPath(d);

        const isMask = group.paths.some(({ path: outerPath }) =>
          points.every(({ x, y }) => {
            const owner = outerPath.ownerSVGElement;

            if (typeof owner?.createSVGPoint !== 'function') return false;

            const point = outerPath.ownerSVGElement?.createSVGPoint();

            if (!point) return false;

            point.x = x;
            point.y = y;

            return outerPath.isPointInFill(point);
          }),
        );

        return {
          ...acc,
          [key]: {
            ...group,
            paths: isMask ? group.paths : [...group.paths, { d, path }],
            masks: isMask ? [...(group.masks ?? []), { d, path }] : group.masks,
          },
        };
      },
      {} as Record<string, PathGroup>,
    );

    return {
      dimensions: extractSvgDimensions(element),
      segments: Object.entries(segments).map(
        ([id, { paths, masks, ...rest }]) => ({
          id,
          ...rest,
          paths: paths.map(({ d }) => d),
          masks: masks?.map(({ d }) => d),
        }),
      ),
    };
  } catch (error) {
    if (__DEV__) {
      throw error;
    } else {
      logError(ensureError(error));
      return { dimensions: { width: 0, height: 0 }, segments: [] };
    }
  }
};
